import { Tooltip } from "./utils-DwC9u0tM.js";
import { Info } from "./icons-ZIeZXXS7.js";
import { Label } from "./internal/components/Label.js";
import { FormGroup } from "./FormGroup-V5kIgQRN.js";
import { useRef } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
import { createFormHookContexts, useStore } from "@tanstack/react-form";
//#region src/blocks/Form/contexts.ts
const { fieldContext, useFieldContext, formContext, useFormContext } = createFormHookContexts();
//#endregion
//#region src/blocks/Form/connectors/FieldItem.tsx
/**
* Shared form-field frame: label, control, help/error text. Owns validity, ids and `aria-*` wiring,
* so a connector only picks a control and binds its value.
*/
const FieldItem = ({ label, action, required, requiredMark = "required", labelling = "for", help, tooltip, className, "aria-describedby": ariaDescribedBy, children }) => {
	const field = useFieldContext();
	const form = useFormContext();
	const { errors, isBlurred } = field.state.meta;
	const submissionAttempts = useStore(form.store, (state) => state.submissionAttempts);
	/**
	* The submit count as of this field's first render.
	*
	* - A bare `> 0` would flag fields mounted after a failed submit.
	* - `form.reset()` zeroes the counter, so the baseline follows it down.
	*/
	const attemptsAtMountRef = useRef(submissionAttempts);
	if (submissionAttempts < attemptsAtMountRef.current) attemptsAtMountRef.current = submissionAttempts;
	/**
	* ⚠️ Blurred, not touched.
	*
	* - `setFieldValue` and `validateField` set `isTouched`, so code-filled fields would show errors.
	* - Only a real blur sets `isBlurred` — and `setServerFieldErrors`, for failures outside a submit.
	* - A submit attempt reveals every field that was on screen for it.
	*/
	const isInvalid = (isBlurred || submissionAttempts > attemptsAtMountRef.current) && errors.length > 0;
	const errorId = `${field.name}-error`;
	const descriptionId = `${field.name}-description`;
	const labelId = `${field.name}-label`;
	const namesByAria = labelling === "aria-labelledby";
	const describedBy = [
		ariaDescribedBy,
		!!help && descriptionId,
		isInvalid && errorId
	].filter(Boolean).join(" ") || void 0;
	return /* @__PURE__ */ jsxs(FormGroup, {
		className,
		children: [
			(label || action) && /* @__PURE__ */ jsxs("div", {
				className: "tw:flex tw:items-center tw:justify-between tw:gap-2",
				children: [/* @__PURE__ */ jsxs("div", {
					className: "tw:flex tw:items-center tw:gap-1",
					children: [label && /* @__PURE__ */ jsxs(Label, {
						htmlFor: namesByAria ? void 0 : field.name,
						id: namesByAria ? labelId : void 0,
						children: [
							label,
							requiredMark === "required" && required && /* @__PURE__ */ jsx(Label.Required, {}),
							requiredMark === "optional" && !required && /* @__PURE__ */ jsx(Label.Optional, {})
						]
					}), tooltip && /* @__PURE__ */ jsx(Tooltip, {
						tooltip,
						placement: "top",
						triggerAsChild: true,
						children: /* @__PURE__ */ jsx("button", {
							type: "button",
							"aria-label": typeof label === "string" ? `More information about ${label}` : "More information",
							className: "tw:text-muted-foreground tw:focus:outline-hidden tw:focus-visible:text-primary tailwindcss-preflight",
							children: /* @__PURE__ */ jsx(Info, { className: "tw:size-3" })
						})
					})]
				}), action]
			}),
			children({
				id: field.name,
				name: field.name,
				"aria-required": required || void 0,
				"aria-invalid": isInvalid || void 0,
				"aria-describedby": describedBy,
				"aria-labelledby": namesByAria && label ? labelId : void 0
			}),
			(help || isInvalid) && /* @__PURE__ */ jsxs("div", { children: [help && /* @__PURE__ */ jsx(FormGroup.Help, {
				id: descriptionId,
				children: help
			}), isInvalid && /* @__PURE__ */ jsx(FormGroup.Error, {
				id: errorId,
				errors
			})] })
		]
	});
};
//#endregion
export { FieldItem, fieldContext, formContext, useFieldContext, useFormContext };

//# sourceMappingURL=FieldItem-BHUqgSO1.js.map